import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Store, 
  MapPin, 
  Clock, 
  Star, 
  ArrowLeft, 
  ShoppingCart, 
  Package,
  Send,
  Camera,
  Quote,
  ThumbsUp,
  User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Star Rating Display Component
const StarRatingDisplay = ({ rating, size = 20, showValue = true }) => {
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  
  return (
    <div className="flex items-center gap-2">
      <div className="flex">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={size}
            className={i < fullStars ? "fill-amber-400 text-amber-400" : (i === fullStars && hasHalf ? "fill-amber-400/50 text-amber-400" : "fill-gray-200 text-gray-200")}
          />
        ))}
      </div>
      {showValue && (
        <span className="font-bold text-lg">{rating?.toFixed(1) || '0.0'}</span>
      )}
    </div>
  );
};

// Interactive Star Rating for Submitting Reviews
const StarRatingInput = ({ value, onChange }) => {
  const [hoverValue, setHoverValue] = useState(0);
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHoverValue(star)}
          onMouseLeave={() => setHoverValue(0)}
          className="p-1 transition-transform hover:scale-110"
          data-testid={`star-rating-${star}`}
        >
          <Star
            size={32}
            className={`transition-colors ${
              star <= (hoverValue || value) 
                ? "fill-amber-400 text-amber-400" 
                : "fill-gray-200 text-gray-200"
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Rating Breakdown Component
const RatingBreakdown = ({ breakdown, totalReviews }) => {
  return (
    <div className="space-y-2">
      {[5, 4, 3, 2, 1].map((star) => {
        const count = breakdown?.[star] || 0;
        const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
        return (
          <div key={star} className="flex items-center gap-3">
            <span className="text-sm font-medium w-4 text-gray-600">{star}</span>
            <Star size={14} className="fill-amber-400 text-amber-400" />
            <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-amber-400 rounded-full transition-all duration-500"
                style={{ width: `${percentage}%` }}
              />
            </div>
            <span className="text-sm text-gray-500 w-10 text-right">{count}</span>
          </div>
        );
      })}
    </div>
  );
};

// Review Card Component
const ReviewCard = ({ review }) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm" data-testid={`review-${review.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
          {review.customer_name?.charAt(0).toUpperCase() || 'U'}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h4 className="font-semibold text-gray-900">{review.customer_name || 'Anonymous'}</h4>
              <p className="text-xs text-gray-500">
                {new Date(review.created_at).toLocaleDateString('en-IN', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
            <StarRatingDisplay rating={review.rating} size={16} showValue={false} />
          </div>
          <p className="text-gray-700 leading-relaxed">{review.comment}</p>
          {review.photo_url && (
            <div className="mt-3">
              <img 
                src={review.photo_url} 
                alt="Review" 
                className="rounded-xl max-h-48 object-cover"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ShopDetail = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  
  // Review form state
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  
  // Rating breakdown calculation
  const [ratingBreakdown, setRatingBreakdown] = useState({});

  useEffect(() => {
    fetchShopData();
  }, [shopId]);

  const fetchShopData = async () => {
    setLoading(true);
    try {
      const [shopRes, productsRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/shops/${shopId}`),
        axios.get(`${API}/products?shop_id=${shopId}`),
        axios.get(`${API}/reviews?shop_id=${shopId}`)
      ]);
      
      setShop(shopRes.data);
      setProducts(productsRes.data || []);
      setReviews(reviewsRes.data || []);
      
      // Calculate rating breakdown
      const breakdown = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      (reviewsRes.data || []).forEach(r => {
        if (r.rating >= 1 && r.rating <= 5) {
          breakdown[Math.floor(r.rating)]++;
        }
      });
      setRatingBreakdown(breakdown);
    } catch (error) {
      console.error('Failed to load shop data:', error);
      toast.error('Failed to load shop details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login to submit a review');
      navigate('/auth');
      return;
    }
    
    if (!reviewComment.trim()) {
      toast.error('Please write a review comment');
      return;
    }
    
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, {
        shop_id: shopId,
        rating: reviewRating,
        comment: reviewComment.trim()
      }, { headers: getAuthHeader() });
      
      toast.success('Review submitted successfully!');
      setShowReviewForm(false);
      setReviewRating(5);
      setReviewComment('');
      fetchShopData(); // Refresh to show new review
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading shop details...</p>
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Shop not found</h2>
          <Link to="/">
            <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const averageRating = shop.rating || (reviews.length > 0 
    ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length 
    : 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Shop Info Card */}
        <div className="bg-white rounded-3xl shadow-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-emerald-500 to-orange-500 h-32 md:h-48" />
          <div className="p-6 md:p-8 -mt-16 relative">
            <div className="flex flex-col md:flex-row md:items-end gap-6">
              {/* Shop Icon */}
              <div className="w-24 h-24 md:w-32 md:h-32 bg-white rounded-2xl shadow-xl flex items-center justify-center border-4 border-white">
                <Store className="w-12 h-12 md:w-16 md:h-16 text-emerald-500" />
              </div>
              
              {/* Shop Details */}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{shop.name}</h1>
                <p className="text-gray-600 mb-3">{shop.description}</p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <MapPin size={16} className="text-orange-500" />
                    <span>{shop.address}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock size={16} className="text-emerald-500" />
                    <span>20-30 min delivery</span>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                {user && (
                  <Link to="/customer/dashboard">
                    <Button 
                      className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full h-12 px-6"
                      data-testid="order-now-btn"
                    >
                      <ShoppingCart className="w-5 h-5 mr-2" />
                      Order Now
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Rating Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-40">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Customer Ratings</h2>
              
              {/* Overall Rating */}
              <div className="text-center mb-6 pb-6 border-b border-gray-100">
                <p className="text-5xl font-black text-gray-900 mb-2">{averageRating.toFixed(1)}</p>
                <StarRatingDisplay rating={averageRating} size={24} showValue={false} />
                <p className="text-gray-500 mt-2">{reviews.length} reviews</p>
              </div>
              
              {/* Rating Breakdown */}
              <div className="mb-6">
                <RatingBreakdown breakdown={ratingBreakdown} totalReviews={reviews.length} />
              </div>
              
              {/* Write Review Button */}
              <Button
                onClick={() => {
                  if (!user) {
                    toast.error('Please login to write a review');
                    navigate('/auth');
                    return;
                  }
                  setShowReviewForm(true);
                }}
                className="w-full bg-gradient-to-r from-emerald-500 to-orange-500 hover:from-emerald-600 hover:to-orange-600 text-white rounded-full h-12"
                data-testid="write-review-btn"
              >
                <Star className="w-5 h-5 mr-2" />
                Write a Review
              </Button>
            </div>
          </div>

          {/* Right Column - Reviews List */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Customer Reviews</h2>
              <span className="text-sm text-gray-500">{reviews.length} reviews</span>
            </div>
            
            {reviews.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <Quote className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No reviews yet</h3>
                <p className="text-gray-500 mb-6">Be the first to review this shop!</p>
                <Button
                  onClick={() => {
                    if (!user) {
                      navigate('/auth');
                      return;
                    }
                    setShowReviewForm(true);
                  }}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"
                  data-testid="first-review-btn"
                >
                  Write First Review
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Review Form Dialog */}
      <Dialog open={showReviewForm} onOpenChange={setShowReviewForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Write a Review</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitReview} className="space-y-6 mt-4">
            {/* Rating Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Your Rating
              </label>
              <div className="flex justify-center">
                <StarRatingInput value={reviewRating} onChange={setReviewRating} />
              </div>
              <p className="text-center text-sm text-gray-500 mt-2">
                {reviewRating === 1 && 'Poor'}
                {reviewRating === 2 && 'Fair'}
                {reviewRating === 3 && 'Good'}
                {reviewRating === 4 && 'Very Good'}
                {reviewRating === 5 && 'Excellent'}
              </p>
            </div>

            {/* Comment Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Review
              </label>
              <Textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Share your experience with this shop... What did you like? How was the service?"
                rows={4}
                className="w-full rounded-xl border-gray-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                data-testid="review-comment-input"
              />
            </div>

            {/* Submit Button */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowReviewForm(false)}
                className="flex-1 h-12 rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submittingReview}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-orange-500 hover:from-emerald-600 hover:to-orange-600 text-white h-12 rounded-full"
                data-testid="submit-review-btn"
              >
                {submittingReview ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send size={18} />
                    Submit Review
                  </span>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShopDetail;
